import type { Ref } from 'react';
import { Checkbox, ListItemText, MenuItem, Select } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';

export interface MultiSelectOption<V extends string> {
  value: V;
  label: string;
}

export interface MultiSelectFilterProps<V extends string> {
  /** Currently selected values (empty = "all"). */
  values: V[];
  /** Fired with the next selection. */
  onChange: (values: V[]) => void;
  /** The selectable options. */
  options: MultiSelectOption<V>[];
  /** Display text when nothing is selected (translated copy). */
  allLabel: string;
  /**
   * Template for the "N selected" summary when 2+ are chosen (translated copy).
   * Receives `{{count}}`; e.g. `"{{count}} selected"`. A single selection shows
   * that option's label instead.
   */
  selectedSummary: (count: number) => string;
  /** Accessible label (translated copy). */
  ariaLabel: string;
  /** Control width. */
  width?: number | string;
  ref?: Ref<HTMLDivElement>;
}

/**
 * Compact multi-select for the {@link FilterBar} (the Polaris kit
 * `MultiSelectInline`): a checkbox dropdown that reads "All …" when empty, the
 * single option when one is picked, and an "N selected" summary otherwise. Built
 * on MUI `Select multiple`; values are opaque strings the caller maps to copy.
 */
export function MultiSelectFilter<V extends string>({
  values,
  onChange,
  options,
  allLabel,
  selectedSummary,
  ariaLabel,
  width = 190,
  ref,
}: MultiSelectFilterProps<V>) {
  const handleChange = (event: SelectChangeEvent<V[]>) => {
    const next = event.target.value;
    onChange(typeof next === 'string' ? (next.split(',') as V[]) : next);
  };

  const renderValue = (selected: V[]): string => {
    if (selected.length === 0) return allLabel;
    if (selected.length === 1) {
      const only = options.find((o) => o.value === selected[0]);
      return only?.label ?? allLabel;
    }
    return selectedSummary(selected.length);
  };

  return (
    <Select<V[]>
      ref={ref}
      multiple
      displayEmpty
      size="small"
      value={values}
      onChange={handleChange}
      renderValue={renderValue}
      inputProps={{ 'aria-label': ariaLabel }}
      sx={{ minWidth: width }}
    >
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value} dense>
          <Checkbox size="small" checked={values.includes(option.value)} sx={{ py: 0.25 }} />
          <ListItemText primary={option.label} />
        </MenuItem>
      ))}
    </Select>
  );
}
